import React, { type ReactElement } from 'react';
import type { QueryResult } from '@apollo/react-common';
import { Query, type QueryComponentOptions } from '@apollo/react-components';
import type { ApolloError } from 'apollo-client';
import gqlTagSwag from 'graphql-tag';
import type {
	GqlCumulativeFlowDiagramChart,
	GqlCumulativeFlowDiagramDataNode,
	GqlCumulativeFlowDiagramFilters,
	GqlCumulativeFlowDiagramFeature,
	Response,
} from './types';
/*!
 * This module contains integration with SWAG's GraphQL nodes for CFD.
 */

/**
 * Turn GraphQL relay connection response onto a flat data list.
 */
export const flattenEdges = (
	connection: GqlCumulativeFlowDiagramChart,
): GqlCumulativeFlowDiagramDataNode[] => {
	const { edges } = connection;
	return edges.map((edge) => edge.node);
};

export const CFD_REPORT_QUERY = gqlTagSwag`
    query CFDReport($boardId: ID!) {
        boardScope(boardId: $boardId) {
            features {
                key
                status
            }
            reports {
                cumulativeFlowDiagram {
                    chart {
                        edges {
                            node {
                                timestamp
                                changes {
                                    key
                                    columnFrom
                                    columnTo
                                    statusTo
                                }

                                columnCounts {
                                    columnIndex
                                    count
                                }
                            }
                        }
                    }

                    filters {
                        columns {
                            name
                        }
                    }
                }
            }
        }
    }
`;

type Variables = {
	boardId: number;
};

type QueryProps = JSX.LibraryManagedAttributes<
	typeof Query,
	QueryComponentOptions<Response, Variables>
>;
type OtherQueryProps = Flow.Diff<
	QueryProps,
	{
		query: QueryProps['query'];
		fetchPolicy?: QueryProps['fetchPolicy'];
	}
>;
type CFDQueryProps = OtherQueryProps & {
	children: (arg1: {
		loading: boolean;
		refetch: QueryResult<Response, Variables>['refetch'];
		error?: ApolloError;
		data: // eslint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<any, any>
			| {
					chart: GqlCumulativeFlowDiagramDataNode[];
					filters: GqlCumulativeFlowDiagramFilters;
					features: GqlCumulativeFlowDiagramFeature[];
			  };
	}) => ReactElement;
};

const EMPTY_DATA = Object.freeze({});

/**
 * Apollo query for the CFD report data.
 */
const CFDQuery = ({ children, ...props }: CFDQueryProps) => (
	<Query<Response, Variables> {...props} query={CFD_REPORT_QUERY} fetchPolicy="cache-and-network">
		{({ data, error, refetch, loading }: QueryResult<Response, Variables>) => {
			const boardScope = data && data.boardScope;
			if (boardScope) {
				return children({
					error,
					loading,
					refetch,
					data: {
						filters: boardScope.reports.cumulativeFlowDiagram.filters,
						chart: flattenEdges(boardScope.reports.cumulativeFlowDiagram.chart),
						features: boardScope.features,
					},
				});
			}

			return children({ data: EMPTY_DATA, error, refetch, loading });
		}}
	</Query>
);

export default CFDQuery;
